import { useUserContext } from '@common/context/user';
import { Button } from '@konova/ui/button';
import { RequireConfirmation, RequireConfirmationChildProps } from '@konova/ui/require-confirmation';
import { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export type AnonymousContextConfirmationModalProps = {
  children: ReactElement<RequireConfirmationChildProps>;
};

/**
 * This component checks if the user is logged in and anonymous. If both conditions are true, it renders a confirmation modal that informs the user about the implications of their anonymous status and asks for confirmation before proceeding. If the user is not logged in or not anonymous, it simply renders the children without any confirmation.
 */
export const AnonymousContextConfirmationModal: FC<AnonymousContextConfirmationModalProps> = ({ children }) => {
  const [t] = useTranslation();
  const userContext = useUserContext();

  if (userContext.isLoggedIn && userContext.isAnonymous) {
    return <RequireConfirmation
      title={t('anonymous.context-modal.title')}
      description={t('anonymous.context-modal.description')}
      actions={({ onCancel, onConfirm }) => (<>
        <Button variant='text' onClick={onCancel}>{t('common.cancel')}</Button>
        <Button onClick={onConfirm}>{t('anonymous.context-modal.confirm')}</Button>
      </>)}>
      {children}
    </RequireConfirmation>;
  }

  return <>{children}</>;
};